import type {
  QueryRiskLevel,
  RequestType,
  RestParam,
  RestContentType,
  RestMethod,
  StoredQuery,
  StoredSecret,
  StoredServer,
  StoredSharedConfigRef,
} from "./persistence";
import type { QueryOperationConfig, ServerConnectionConfig, ServerKind } from "@spuerhund/shared/targets";
export type { StoredSecret, StoredSharedConfigRef } from "./persistence";

export type SharedConfigRole = "owner" | "editor";
export type SharedVaultRefKind = "secret" | "config";

export interface SharedConfigRequiredVaultRef {
  kind: SharedVaultRefKind;
  key: string;
}

export interface SharedConfigValue {
  key: string;
  value: string;
  scopeType: "global" | "server";
  description?: string;
}

export interface SharedConfigMember {
  clientId: string;
  displayName: string;
  role: SharedConfigRole;
  joinedAt: string;
  lastSeenAt: string;
}

export interface SharedConfigPayloadQuery {
  id: string;
  name: string;
  queryText: string;
  variables: string;
  headers: string;
  remarks?: string;
  riskLevel?: QueryRiskLevel;
  category?: string;
  pinnedAt?: string | null;
  requestType?: RequestType;
  operationConfig?: QueryOperationConfig;
  restMethod?: RestMethod;
  restPath?: string;
  restParams?: RestParam[];
  restContentType?: RestContentType;
}

interface SharedConfigPayloadServerBase {
  name: string;
  serverKind?: ServerKind;
  connectionConfig?: ServerConnectionConfig;
  authType: StoredServer["authType"];
  authConfig: string;
  isProduction?: boolean;
  timeoutMs?: number;
}

export interface SharedConfigPayloadV1 {
  schemaVersion: 1;
  server: SharedConfigPayloadServerBase & {
    endpoint: string;
  };
  queries: SharedConfigPayloadQuery[];
  notes?: string;
  requiredSecrets: string[];
}

export interface SharedConfigPayloadV2 {
  schemaVersion: 2;
  server: SharedConfigPayloadServerBase & {
    baseUrl: string;
    graphqlPath?: string;
    restPath?: string;
  };
  queries: SharedConfigPayloadQuery[];
  notes?: string;
  requiredSecrets: string[];
}

export interface SharedConfigPayloadV3 {
  schemaVersion: 3;
  server: SharedConfigPayloadServerBase & {
    baseUrl: string;
    graphqlPath?: string;
    restPath?: string;
  };
  queries: SharedConfigPayloadQuery[];
  notes?: string;
  requiredVaultRefs: SharedConfigRequiredVaultRef[];
}

export interface SharedConfigPayloadV4 {
  schemaVersion: 4;
  server: SharedConfigPayloadServerBase & {
    baseUrl: string;
    graphqlPath?: string;
    restPath?: string;
  };
  queries: SharedConfigPayloadQuery[];
  notes?: string;
  requiredVaultRefs: SharedConfigRequiredVaultRef[];
  sharedConfigs: SharedConfigValue[];
}

export interface SharedConfigPayloadV5 {
  schemaVersion: 5;
  server: SharedConfigPayloadServerBase & {
    baseUrl: string;
    graphqlPath?: string;
    restPath?: string;
  };
  queries: SharedConfigPayloadQuery[];
  notes?: string;
  requiredVaultRefs: SharedConfigRequiredVaultRef[];
  sharedConfigs: SharedConfigValue[];
}

export interface SharedConfigPayloadV6 {
  schemaVersion: 6;
  server: SharedConfigPayloadServerBase & {
    baseUrl: string;
    graphqlPath?: string;
    restPath?: string;
  };
  queries: SharedConfigPayloadQuery[];
  notes?: string;
  requiredVaultRefs: SharedConfigRequiredVaultRef[];
  sharedConfigs: SharedConfigValue[];
}

export type SharedConfigPayload = SharedConfigPayloadV1 | SharedConfigPayloadV2 | SharedConfigPayloadV3 | SharedConfigPayloadV4 | SharedConfigPayloadV5 | SharedConfigPayloadV6;

export interface SharedConfigApiDocument {
  configId: string;
  ciphertext: string;
  version: number;
  updatedAt: string;
  updatedByClientId: string;
  updatedByDisplayName?: string | null;
}

export interface SharedConfigCreateResponse extends SharedConfigApiDocument {
  ownerSecret: string;
  editorInvite: string;
  createdAt: string;
}

export interface SharedConfigChangesResponse {
  currentVersion: number;
  hasChanges: boolean;
  updatedAt: string;
  updatedByClientId: string;
  updatedByDisplayName?: string | null;
}

export interface SharedConfigStreamReadyEvent {
  event: "ready";
  currentVersion: number;
  updatedAt: string;
  updatedByClientId: string;
  updatedByDisplayName?: string | null;
}

export interface SharedConfigStreamChangedEvent {
  event: "changed";
  currentVersion: number;
  updatedAt: string;
  updatedByClientId: string;
  updatedByDisplayName?: string | null;
}

export interface SharedConfigStreamDeletedEvent {
  event: "deleted";
  currentVersion: number;
  updatedAt: string;
  updatedByClientId: string;
  updatedByDisplayName?: string | null;
}

export type SharedConfigStreamEvent =
  | SharedConfigStreamReadyEvent
  | SharedConfigStreamChangedEvent
  | SharedConfigStreamDeletedEvent;

export interface SharedConfigJoinResponse {
  role: SharedConfigRole;
  version: number;
  updatedAt: string;
  updatedByDisplayName?: string | null;
}

export interface SharedConfigMembersResponse {
  members: SharedConfigMember[];
}

export interface SharedConfigExecutionContext {
  env: Record<string, string>;
  fetch: typeof fetch;
}

export interface SharedConfigShareLink {
  configId: string;
  invite: string;
  key: string;
}

export interface SecretExportBundle {
  schemaVersion: 1;
  exportedAt: string;
  iv: string;
  salt: string;
  ciphertext: string;
}

export function toSharedPayload(
  server: StoredServer,
  queries: StoredQuery[],
  requiredVaultRefs: SharedConfigRequiredVaultRef[],
  sharedConfigs: SharedConfigValue[],
): SharedConfigPayloadV6 {
  const savedQueries = queries.filter((query) => !query.systemPreset);
  return {
    schemaVersion: 6,
    server: {
      name: server.name,
      ...(server.serverKind ? { serverKind: server.serverKind } : {}),
      ...(server.connectionConfig ? { connectionConfig: server.connectionConfig } : {}),
      baseUrl: server.baseUrl,
      ...(server.graphqlPath != null ? { graphqlPath: server.graphqlPath } : {}),
      ...(server.restPath != null ? { restPath: server.restPath } : {}),
      authType: server.authType,
      authConfig: server.authConfig,
      ...(server.isProduction != null ? { isProduction: server.isProduction } : {}),
      ...(server.timeoutMs != null ? { timeoutMs: server.timeoutMs } : {}),
    },
    queries: savedQueries.map((query) => ({
      id: query.id,
      name: query.name,
      queryText: query.queryText,
      variables: query.variables,
      headers: query.headers,
      ...(query.remarks ? { remarks: query.remarks } : {}),
      ...(query.riskLevel ? { riskLevel: query.riskLevel } : {}),
      ...(query.category ? { category: query.category } : {}),
      ...(query.pinnedAt !== undefined ? { pinnedAt: query.pinnedAt } : {}),
      ...(query.requestType ? { requestType: query.requestType } : {}),
      ...(query.operationConfig ? { operationConfig: query.operationConfig } : {}),
      ...(query.restMethod ? { restMethod: query.restMethod } : {}),
      ...(query.restPath != null ? { restPath: query.restPath } : {}),
      ...(query.restParams ? { restParams: query.restParams } : {}),
      ...(query.restContentType ? { restContentType: query.restContentType } : {}),
    })),
    requiredVaultRefs,
    sharedConfigs,
  };
}

function splitEndpoint(endpoint: string): { baseUrl: string; graphqlPath?: string } {
  const trimmed = endpoint.trim();
  if (!trimmed) {
    return { baseUrl: "" };
  }

  try {
    const url = new URL(trimmed);
    const path = `${url.pathname}${url.search}${url.hash}`;
    return {
      baseUrl: url.origin,
      ...(path && path !== "/" ? { graphqlPath: path } : {}),
    };
  } catch {
    return { baseUrl: trimmed };
  }
}

export function resolveSharedPayloadServer(
  server: SharedConfigPayload["server"],
): Pick<StoredServer, "name" | "baseUrl" | "authType" | "authConfig">
  & Partial<Pick<StoredServer, "serverKind" | "connectionConfig" | "graphqlPath" | "restPath" | "isProduction" | "timeoutMs">> {
  if ("baseUrl" in server) {
    return {
      name: server.name,
      ...(server.serverKind ? { serverKind: server.serverKind } : {}),
      ...(server.connectionConfig ? { connectionConfig: server.connectionConfig } : {}),
      baseUrl: server.baseUrl,
      authType: server.authType,
      authConfig: server.authConfig,
      ...(server.graphqlPath != null ? { graphqlPath: server.graphqlPath } : {}),
      ...(server.restPath != null ? { restPath: server.restPath } : {}),
      ...(server.isProduction != null ? { isProduction: server.isProduction } : {}),
      ...(server.timeoutMs != null ? { timeoutMs: server.timeoutMs } : {}),
    };
  }

  const migrated = splitEndpoint(server.endpoint);
  return {
    name: server.name,
    baseUrl: migrated.baseUrl,
    authType: server.authType,
    authConfig: server.authConfig,
    ...(migrated.graphqlPath != null ? { graphqlPath: migrated.graphqlPath } : {}),
    ...(server.isProduction != null ? { isProduction: server.isProduction } : {}),
    ...(server.timeoutMs != null ? { timeoutMs: server.timeoutMs } : {}),
  };
}

export function getSharedPayloadRequiredVaultRefs(
  payload: SharedConfigPayload,
): SharedConfigRequiredVaultRef[] {
  if ("requiredVaultRefs" in payload) {
    return dedupeRequiredVaultRefs(payload.requiredVaultRefs);
  }

  return dedupeRequiredVaultRefs(
    payload.requiredSecrets.map((key) => ({ kind: "secret" as const, key })),
  );
}

export function getSharedPayloadSharedConfigs(
  payload: SharedConfigPayload,
): SharedConfigValue[] {
  if (!("sharedConfigs" in payload) || !Array.isArray(payload.sharedConfigs)) {
    return [];
  }

  const deduped = new Map<string, SharedConfigValue>();
  for (const entry of payload.sharedConfigs) {
    const key = typeof entry.key === "string" ? entry.key.trim() : "";
    if (!key) continue;
    const value = typeof entry.value === "string" ? entry.value : String(entry.value ?? "");
    const scopeType = entry.scopeType === "server" ? "server" : "global";
    const id = `${scopeType}:${key}`;
    deduped.set(id, {
      key,
      value,
      scopeType,
      ...(typeof entry.description === "string" && entry.description.trim()
        ? { description: entry.description.trim() }
        : {}),
    });
  }

  return [...deduped.values()].sort((a, b) =>
    a.scopeType === b.scopeType
      ? a.key.localeCompare(b.key, undefined, { sensitivity: "base" })
      : a.scopeType.localeCompare(b.scopeType),
  );
}

function dedupeRequiredVaultRefs(
  refs: SharedConfigRequiredVaultRef[],
): SharedConfigRequiredVaultRef[] {
  const seen = new Set<string>();
  const deduped: SharedConfigRequiredVaultRef[] = [];

  for (const ref of refs) {
    const key = ref.key.trim();
    if (!key) continue;
    const id = `${ref.kind}:${key}`;
    if (seen.has(id)) continue;
    seen.add(id);
    deduped.push({ kind: ref.kind, key });
  }

  return deduped.sort((a, b) =>
    a.kind === b.kind
      ? a.key.localeCompare(b.key, undefined, { sensitivity: "base" })
      : a.kind.localeCompare(b.kind),
  );
}

export function parseAuthConfig(authConfig: string): {
  token?: string;
  useScript?: boolean;
  tokenScript?: string;
  headerName?: string;
  apiKey?: string;
  customHeaders?: Record<string, string>;
} {
  try {
    const parsed = JSON.parse(authConfig || "{}");
    if (typeof parsed !== "object" || parsed == null) return {};
    return parsed as ReturnType<typeof parseAuthConfig>;
  } catch {
    return {};
  }
}
